<template>
    <LoadingDiv v-if="isLoading" />

    <template v-else>
        <section class="provider-settings-shell">
            <div class="provider-settings-tabs" role="tablist" aria-label="Provider settings sections">
                <button
                    type="button"
                    class="provider-settings-tab"
                    :class="{ 'is-active': activeSettingsTab === 'inApp' }"
                    @click="activeSettingsTab = 'inApp'"
                >
                    <i class="material-icons">admin_panel_settings</i>
                    <span>In-App</span>
                </button>
                <button
                    type="button"
                    class="provider-settings-tab"
                    :class="{ 'is-active': activeSettingsTab === 'oneProvider' }"
                    @click="activeSettingsTab = 'oneProvider'"
                >
                    <i class="material-icons">sync</i>
                    <span>oneProvider</span>
                </button>
            </div>

            <template v-if="activeSettingsTab === 'inApp'">
                <section class="provider-message-card" v-if="provider?.email">
                    <div class="provider-message-head">
                        <div>
                            <label class="active teal-text">Portal Login</label>
                            <h6>{{ provider?.name }}</h6>
                        </div>
                        <span class="provider-settings-chip is-ready">Email available</span>
                    </div>

                    <div class="provider-message-body">
                        <p>Dear {{ provider?.name }},</p>
                        <p>
                            You're now live on our platform for authorization, claims, and encounter management.
                            Use the credentials below to log in and connect with {{ HMO }}.
                        </p>

                        <div class="provider-credential-grid">
                            <div>
                                <span>Portal Link</span>
                                <strong>{{ appURL }}</strong>
                            </div>
                            <div>
                                <span>Username</span>
                                <strong>{{ provider?.user_created === 'y' ? provider?.users?.[0]?.email : provider?.email }}</strong>
                            </div>
                            <div>
                                <span>Password</span>
                                <strong>{{ temporaryPassword || "********" }}</strong>
                                <button
                                    type="button"
                                    class="waves-effect waves-light btn provider-portal-reset"
                                    :class="{ disabled: isResettingLogin }"
                                    @click="handlePortalLoginReset"
                                >
                                    <i class="material-icons right">vpn_key</i>
                                    {{ isResettingLogin ? "Preparing..." : "Generate / Reset" }}
                                </button>
                            </div>
                        </div>

                        <p>Regards,<br />{{ HMO }}</p>
                    </div>
                </section>

                <section class="provider-message-card is-warning" v-else>
                    <div class="provider-message-head">
                        <div>
                            <label class="active teal-text">Portal Login</label>
                            <h6>{{ provider?.name }}</h6>
                        </div>
                        <span class="provider-settings-chip is-warning">Missing email</span>
                    </div>
                    <div class="provider-message-body">
                        <p>
                            Official email address is not available for <strong>{{ provider?.name }}</strong>.
                            Add an email address in the profile section before sending portal login guidance.
                        </p>
                    </div>
                </section>
            </template>

            <template v-if="activeSettingsTab === 'oneProvider'">
                <section class="provider-message-card" v-if="!provider?.opid">
                    <div class="provider-message-head">
                        <div>
                            <label class="active teal-text">oneProvider Signup</label>
                            <h6>{{ provider?.name }}</h6>
                        </div>
                        <span class="provider-settings-chip is-pending">Sync pending</span>
                    </div>

                    <div class="provider-message-body">
                        <div class="provider-signup-note">
                            <p>Dear {{ provider?.name }},</p>
                            <p>
                                We are live on the oneProvider platform for authorization, claims, and encounter management.
                            </p>
                            <p>
                                Use the signup link below to join oneProvider and connect with {{ HMO }}.
                            </p>
                            <div class="provider-link-box">
                                {{ signupLink || "Signup link is not available yet." }}
                            </div>
                        </div>
                        <button
                            class="waves-effect waves-light btn provider-update-sync"
                            :class="{ disabled: isLoadingSignupLink || !signupLink }"
                            @click="handleCopySignupLink"
                        >
                            <i class="material-icons right">content_copy</i>
                            {{ isLoadingSignupLink ? "Loading..." : "Copy Signup Link" }}
                        </button>
                        <p class="provider-settings-note is-success" v-if="success">{{ success }}</p>
                        <div class="provider-signup-regards">
                            <p>Regards,</p>
                            <p><strong>{{ HMO }}</strong></p>
                        </div>
                    </div>
                </section>

                <template v-else>
                    <p class="provider-settings-note is-success" v-if="success">{{ success }}</p>
                    <p class="provider-settings-note is-error" v-if="error">{{ error }}</p>

                    <section class="provider-settings-card">
                        <div class="provider-settings-card-head">
                            <div>
                                <label class="active teal-text">Provider Schemes</label>
                                <h6>Active oneProvider schemes</h6>
                                <p>Select the schemes this provider should operate under on oneProvider.</p>
                            </div>
                            <span class="provider-settings-chip">{{ schemes.length }} selected</span>
                        </div>

                        <div class="provider-settings-options">
                            <label class="provider-settings-option" v-for="scheme in allSchemes" :key="scheme.id">
                                <input
                                    type="checkbox"
                                    class="filled-in"
                                    :value="scheme.id"
                                    v-model="schemes"
                                    @click="runWatch = true"
                                />
                                <span>
                                    <strong>{{ scheme.name }}</strong>
                                    <small>{{ scheme.type === 'p' ? 'Private Scheme' : 'Social Scheme' }}</small>
                                </span>
                            </label>
                        </div>
                    </section>

                    <section class="provider-settings-card is-muted">
                        <div class="provider-settings-card-head">
                            <div>
                                <label class="active teal-text">Enrollee List Mandate</label>
                                <h6>Required enrollee lists</h6>
                                <p>Mark the active schemes where this provider must receive an enrollee list.</p>
                            </div>
                            <span class="provider-settings-chip">{{ lists.length }} required</span>
                        </div>

                        <div class="provider-settings-options" v-if="schemes.length">
                            <label class="provider-settings-option" v-for="list in schemes" :key="list">
                                <input type="checkbox" class="filled-in" :value="list" v-model="lists" />
                                <span>
                                    <strong>{{ schemeName(list) }}</strong>
                                    <small>{{ lists.find(x => x === list) ? "Enrollee list required" : "No list mandate" }}</small>
                                </span>
                            </label>
                        </div>
                        <p class="provider-settings-empty" v-else>Select at least one provider scheme first.</p>
                    </section>

                    <section class="provider-settings-card is-muted">
                        <div class="provider-settings-card-head">
                            <div>
                                <label class="active teal-text">Claims Vetting Mandate</label>
                                <h6>Vetting handled in this workspace</h6>
                                <p>Mark the active schemes where claims vetting should remain in this workspace.</p>
                            </div>
                            <span class="provider-settings-chip">{{ vettings.length }} assigned</span>
                        </div>

                        <div class="provider-settings-options" v-if="schemes.length">
                            <label class="provider-settings-option" v-for="vet in schemes" :key="vet">
                                <input type="checkbox" class="filled-in" :value="vet" v-model="vettings" />
                                <span>
                                    <strong>{{ schemeName(vet) }}</strong>
                                    <small>{{ vettings.find(x => x === vet) ? "Vet in this workspace" : "Late sync after oneProvider vetting" }}</small>
                                </span>
                            </label>
                        </div>
                        <p class="provider-settings-empty" v-else>Select at least one provider scheme first.</p>
                    </section>

                    <div class="progress provider-settings-progress" v-if="isSubmitting">
                        <div class="indeterminate"></div>
                    </div>

                    <section class="provider-settings-actions">
                        <div>
                            <span>Sync controls</span>
                            <small>Update the provider sync scope or disconnect the oneProvider link.</small>
                        </div>
                        <div>
                            <button class="waves-effect waves-light btn-flat provider-delete-sync" :class="{
                                disabled: isSubmitting,
                            }" @click.prevent="handleDelete">
                                Delete Sync
                            </button>
                            <button class="waves-effect waves-light btn provider-update-sync" :class="{
                                disabled: isSubmitting || !schemes.length
                            }" @click="handleUpdateSync">
                                <i class="material-icons right">sync</i>
                                {{ isSubmitting ? "Re-syncing..." : "Update Sync" }}
                            </button>
                        </div>
                    </section>
                </template>
            </template>
        </section>
    </template>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import getLocalItem from "@/composables/getItem";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import putItem from "@/composables/putItem";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { HMO, OPID, oneProviderURL, appURL } from '@/laravel/config';
import nativeUi from "@/ui/native-ui";


export default {
    name: "SettingsProviderComponent",
    components: { LoadingDiv },
    props: ["easyHMOProvider"],
    emits: ["reload"],
    setup(props, context) {
        const response = ref(props.easyHMOProvider);
        const responseSchemes = ref(null);
        const isReady = ref(true);
        const isLoading = ref(0);
        const isSubmitting = ref(false);
        const schemes = ref([]);
        const lists = ref([]);
        const vettings = ref([]);
        const runWatch = ref(false);
        const error = ref(null);
        const success = ref(null);
        const isLoadingSignupLink = ref(false);
        const isResettingLogin = ref(false);
        const temporaryPassword = ref(null);
        const activeSettingsTab = ref("inApp");

        // starter
        onMounted(() => {
            // start loading
            isLoading.value++;

            (async () => {
                const { res, err } = await getItems("scheme");

                if (res.value) {

                    // schemes
                    responseSchemes.value = res.value.data;

                } else {
                    error.value = err.value;
                }

                // page is ready
                isLoading.value--;

            })();
        });

        // starter
        onMounted(() => {
            // Unsynced providers do not have a oneProvider record yet.
            if (!provider.value?.opid) return;
            
            // start loading
            isLoading.value++;

            // get oneProvider
            (async () => {
                const { res, err } = await getLocalItem(`provider/${provider.value?.id}/oneprovider`, "status");

                if (res.value) {
                    if (res.value.data.pivot.active_schemes) {
                        // get active schemes
                        schemes.value = res.value.data.pivot.active_schemes;
                    }
                    if (res.value.data.pivot.manual_claims) {
                        // get scheme with enrollee lists
                        lists.value = schemes.value?.filter(s => !res.value.data.pivot.manual_claims?.includes(s));
                    }
                    if (res.value.data.pivot.late_sync_claims) {
                        // get scheme with vetting on easyHMO
                        vettings.value = schemes.value?.filter(s => !res.value.data.pivot.late_sync_claims?.includes(s));
                    }

                    // start watching
                    // runWatch.value = true;

                } else {
                    error.value = err.value;
                }

                // end
                // page is ready
                isLoading.value--;

            })();

        });

        // delete
        const handleDelete = async () => {

            // start
            isSubmitting.value = true;

            // update
            let newVal = {
                oneprovider: {
                    status: "p",
                    schemes: [],
                    claims: [],
                    sync: [],
                },
                easyhmo: {
                    opid: null,
                }
            };

            // put item
            const { res, err } = await putItem("provider/sync/update", provider.value.id, newVal);


            if (res.value) {
                // reset provider
                response.value = res.value.data;
                // update
                context.emit("reload", true);
            } else {
                error.value = err.value;
            }

            // end
            isSubmitting.value = false;
        }

        // Sync
        const handleUpdateSync = async () => {

            // start
            isSubmitting.value = true;

            // Manual Claims
            const manual_claims = schemes.value.filter(s => !lists.value.includes(s));

            // Vettings
            const late_sync_claims = schemes.value.filter(s => !vettings.value.includes(s));

            // update
            let newVal = {
                oneprovider: {
                    status: "a",
                    schemes: schemes.value,
                    claims: manual_claims.length ? manual_claims : [],
                    sync: late_sync_claims.length ? late_sync_claims : [],
                },
            };

            // put item
            const { res, err } = await putItem("provider/sync/update", provider.value.id, newVal);

            if (res.value) {
                // success
                success.value = "Success! Provider Settings Updated.";

                // notify (Toast)
          nativeUi.toast({
            html: "Settings Updated <i class='material-icons'>check_circle</i>", // Toast content
            classes: 'green', // Add custom classes // green rounded
            displayLength: 5000, // Duration in milliseconds
            inDuration: 300, // Transition in duration
            outDuration: 300, // Transition out duration
          });

            } else {
                error.value = err.value;
            }

            // end
            isSubmitting.value = false;
        };

        const handleCopySignupLink = async () => {
            error.value = null;
            success.value = null;

            if (!signupLink.value) {
                error.value = "Signup link is not available.";
                return;
            }

            try {
                await navigator.clipboard.writeText(signupLink.value);
                success.value = "Signup link copied.";
            } catch {
                success.value = "Signup link is ready to share.";
            }

            nativeUi.toast({
                html: "Signup Link Ready <i class='material-icons'>check_circle</i>",
                classes: 'green',
                displayLength: 5000,
                inDuration: 300,
                outDuration: 300,
            });
        };

        const handlePortalLoginReset = async () => {
            if (isResettingLogin.value || !provider.value?.id) return;

            error.value = null;
            success.value = null;
            temporaryPassword.value = null;
            isResettingLogin.value = true;

            const { res, err } = await postItem(`provider/${provider.value.id}/portal-login/reset`, {});

            if (res.value?.data) {
                temporaryPassword.value = res.value.data.password;
                response.value = res.value.data.provider;
                success.value = "Provider login is ready.";

                nativeUi.toast({
                    html: "Login Ready <i class='material-icons'>check_circle</i>",
                    classes: 'green',
                    displayLength: 5000,
                    inDuration: 300,
                    outDuration: 300,
                });
            } else {
                error.value = err.value || "Provider login could not be prepared.";
            }

            isResettingLogin.value = false;
        };


        // watch lists (schemes)
        watch(schemes, (newSchemes, oldSchemes) => {
            // checker
            if (!runWatch.value) return;

            // Added items
            const added = newSchemes.filter(n => !oldSchemes.includes(n));
            // Removed items
            const removed = oldSchemes.filter(n => !newSchemes.includes(n));

            // Add newly selected numbers
            added.forEach(n => {
                if (!lists.value.includes(n)) {
                    lists.value.push(n)
                }
            });

            // Remove deselected numbers
            removed.forEach(n => {
                const index = lists.value.indexOf(n)
                if (index !== -1) {
                    lists.value.splice(index, 1)
                }
            });

        })

        // watch vetting (schemes)
        watch(schemes, (newSchemes, oldSchemes) => {
            // checker
            if (!runWatch.value) return;

            // Added items
            const added = newSchemes.filter(n => !oldSchemes.includes(n));
            // Removed items
            const removed = oldSchemes.filter(n => !newSchemes.includes(n));

            // Add newly selected numbers
            added.forEach(n => {
                if (!vettings.value.includes(n)) {
                    vettings.value.push(n)
                }
            });

            // Remove deselected numbers
            removed.forEach(n => {
                const index = vettings.value.indexOf(n)
                if (index !== -1) {
                    vettings.value.splice(index, 1)
                }
            });

        })

        // provider
        const provider = computed(() => {
            if (!response.value) {
                return [];
            } else {
                return response.value;
            }
        });

        // output results
        const allSchemes = computed(() => {
            if (!responseSchemes.value) {
                return [];
            } else {
                return responseSchemes.value;
            }
        });

        const schemeName = (id) => {
            return allSchemes.value.find(x => x.id === id)?.name || "Scheme";
        };

        const oneProviderSignupBase = computed(() => {
            return String(oneProviderURL || "").replace(/\/$/, "");
        });

        const signupLink = computed(() => {
            if (!OPID || !oneProviderSignupBase.value) {
                return null;
            }

            return `${oneProviderSignupBase.value}/payer/provider/signup/${encodeURIComponent(OPID)}`;
        });

        return {
            handleDelete,
            handleUpdateSync,
            handleCopySignupLink,
            handlePortalLoginReset,
            activeSettingsTab,
            schemeName,
            isReady,
            isLoading,
            isSubmitting,
            isLoadingSignupLink,
            isResettingLogin,
            temporaryPassword,
            runWatch,
            success,
            error,
            signupLink,
            lists,
            vettings,
            provider,
            allSchemes,
            schemes,
            HMO,
            appURL: appURL,
        };
    },
};
</script>

<style scoped>
.provider-settings-shell {
    color: #263238;
    display: grid;
    gap: 14px;
}

.provider-settings-tabs {
    align-items: center;
    background: #ffffff;
    border: 1px solid #d9e5e9;
    border-radius: 6px;
    display: inline-flex;
    gap: 6px;
    justify-self: start;
    padding: 6px;
}

.provider-settings-tab {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 5px;
    color: #546e7a;
    cursor: pointer;
    display: inline-flex;
    font: inherit;
    font-weight: 700;
    gap: 8px;
    height: 42px;
    line-height: 1;
    padding: 0 16px;
}

.provider-settings-tab i {
    color: #00897b;
    font-size: 1.15rem;
}

.provider-settings-tab.is-active {
    background: #263238;
    color: #ffffff;
}

.provider-settings-tab.is-active i {
    color: #ffffff;
}

.provider-message-card,
.provider-settings-card,
.provider-settings-actions {
    background: #ffffff;
    border: 1px solid #e3eaed;
    border-radius: 6px;
    box-shadow: none;
}

.provider-message-head label,
.provider-settings-card-head label {
    display: block;
    font-size: 0.82rem;
    font-weight: 600;
    margin-bottom: 8px;
}

.provider-message-head h6,
.provider-settings-card-head h6 {
    color: #263238;
    font-size: 1.08rem;
    font-weight: 600;
    line-height: 1.35;
    margin: 0;
}

.provider-message-body p,
.provider-settings-card-head p,
.provider-settings-empty,
.provider-settings-actions small {
    color: #607d8b;
    line-height: 1.55;
    margin: 7px 0 0;
}

.provider-settings-chip {
    background: #fafafa;
    border: 1px solid #eeeeee;
    border-radius: 999px;
    color: #455a64;
    flex: 0 0 auto;
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.2;
    padding: 8px 11px;
}

.provider-settings-chip.is-ready {
    background: #e8f3f2;
    border-color: #9bd2cb;
    color: #00695c;
}

.provider-settings-chip.is-warning,
.provider-settings-chip.is-pending {
    background: #fff8e1;
    border-color: #ffe082;
    color: #795548;
}

.provider-message-card.is-warning {
    background: #fffdf8;
    border-color: #ffe0b2;
}

.provider-message-head,
.provider-settings-card-head {
    align-items: flex-start;
    border-bottom: 1px solid #eeeeee;
    display: flex;
    gap: 18px;
    justify-content: space-between;
    padding: 18px 20px;
}

.provider-message-body {
    padding: 20px;
}

.provider-signup-note {
    background: #fbfcfc;
    border: 1px solid #e3eaed;
    border-radius: 6px;
    margin-bottom: 18px;
    padding: 18px;
}

.provider-signup-note p {
    color: #455a64;
    font-size: 1rem;
    line-height: 1.7;
    margin: 0 0 13px;
}

.provider-signup-note p:first-child {
    color: #263238;
}

.provider-signup-note p:last-of-type {
    margin-bottom: 16px;
}

.provider-signup-regards {
    margin-top: 18px;
}

.provider-signup-regards p {
    color: #263238;
    line-height: 1.45;
    margin: 0 0 4px;
}

.provider-message-body p:first-child,
.provider-message-body p:last-child {
    color: #263238;
}

.provider-credential-grid {
    border: 1px solid #eeeeee;
    border-radius: 6px;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin: 18px 0;
    overflow: hidden;
}

.provider-credential-grid div {
    background: #fafafa;
    border-left: 1px solid #eeeeee;
    display: grid;
    gap: 6px;
    padding: 14px;
}

.provider-credential-grid div:first-child {
    border-left: 0;
}

.provider-credential-grid span {
    color: #607d8b;
    font-size: 0.74rem;
    font-weight: 700;
    text-transform: uppercase;
}

.provider-credential-grid strong,
.provider-link-box {
    color: #263238;
    font-weight: 700;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.provider-link-box {
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 6px;
    margin: 0;
    padding: 14px;
}

.provider-settings-note {
    border-radius: 6px;
    font-weight: 700;
    margin: 0;
    max-width: max-content;
    padding: 9px 12px;
}

.provider-settings-note.is-success {
    background: #e8f5e9;
    color: #1b5e20;
}

.provider-settings-note.is-error {
    background: #ffebee;
    color: #b71c1c;
}

.provider-settings-card.is-muted {
    background: #fbfcfc;
}

.provider-settings-options {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 18px 20px;
}

.provider-settings-option {
    align-items: flex-start;
    background: #ffffff;
    border: 1px solid #e8eef0;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    gap: 12px;
    min-height: 74px;
    padding: 13px 14px;
}

.provider-settings-option [type="checkbox"] + span:not(.lever) {
    color: #263238;
    font-size: 0.95rem;
    line-height: 1.4;
    min-height: auto;
    padding-left: 30px;
}

.provider-settings-option strong {
    display: block;
    font-weight: 700;
}

.provider-settings-option small {
    color: #607d8b;
    display: block;
    font-size: 0.82rem;
    margin-top: 4px;
}

.provider-settings-empty {
    padding: 0 20px 18px;
}

.provider-settings-progress {
    margin: -4px 0 0;
}

.provider-settings-actions {
    align-items: center;
    background: #fafafa;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    padding: 16px 18px;
}

.provider-settings-actions span {
    color: #263238;
    display: block;
    font-weight: 700;
}

.provider-settings-actions div:last-child {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-end;
}

.provider-delete-sync {
    border: 1px solid #ffcdd2;
    border-radius: 6px;
    color: #b71c1c;
    font-weight: 700;
    text-transform: none;
}

.provider-update-sync {
    background: #263238;
    border-radius: 6px;
    box-shadow: none;
    font-weight: 700;
    text-transform: none;
}

.provider-portal-reset {
    align-items: center;
    background: #263238;
    border-radius: 6px;
    box-shadow: none;
    display: inline-flex;
    font-size: 0.82rem;
    font-weight: 700;
    height: 36px;
    line-height: 36px;
    margin-top: 8px;
    padding: 0 14px;
    text-transform: none;
    width: max-content;
}

.provider-portal-reset i.right {
    margin-left: 8px;
}

@media only screen and (max-width: 900px) {
    .provider-message-head,
    .provider-settings-card-head,
    .provider-settings-actions {
        align-items: flex-start;
        flex-direction: column;
    }

    .provider-credential-grid,
    .provider-settings-options {
        grid-template-columns: 1fr;
    }

    .provider-credential-grid div {
        border-left: 0;
        border-top: 1px solid #eeeeee;
    }

    .provider-credential-grid div:first-child {
        border-top: 0;
    }

    .provider-settings-actions div:last-child {
        justify-content: flex-start;
    }

    .provider-settings-tabs {
        display: grid;
        grid-template-columns: 1fr;
        justify-self: stretch;
    }

    .provider-settings-tab {
        justify-content: center;
        width: 100%;
    }
}
</style>
